/**
 * Moteur de dés Vampire la Mascarade V5.
 * Fonctions pures : le hasard est injecté via `rng` (par défaut Math.random) pour être testable.
 */

export const SUCCESS_THRESHOLD = 6;
export const MAX_HUNGER = 5;
export const MAX_WILLPOWER_REROLL = 3;

export const OUTCOMES = {
  MESSY_CRITICAL: 'messyCritical',
  CRITICAL_WIN: 'criticalWin',
  WIN: 'win',
  FAILURE: 'failure',
  TOTAL_FAILURE: 'totalFailure',
  BESTIAL_FAILURE: 'bestialFailure',
};

// Index = Puissance du Sang (0 à 10)
const BLOOD_SURGE_BONUS = [1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6];
const ROUSE_REROLL_MAX_LEVEL = [0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5];
// Dégâts superficiels de Santé soignés par test d'exaltation
const MEND_AMOUNT = [1, 1, 2, 2, 3, 3, 3, 3, 4, 4, 5];

const clampBloodPotency = (bloodPotency) => Math.min(10, Math.max(0, bloodPotency || 0));

export const rollD10 = (rng = Math.random) => Math.floor(rng() * 10) + 1;

const rollDice = (count, rng) => Array.from({length: count}, () => rollD10(rng));

const isSuccess = (value) => value >= SUCCESS_THRESHOLD;

export const SPECIALTY_BONUS = 1;
export const IMPAIRED_PENALTY = -2;

/**
 * Compose une réserve de dés et garde le détail pour l'affichage.
 * @param {Object} options
 * @param {{label: String, value: Number}[]} options.base ex : [Charisme 4, Persuasion 3] ou [Astuce 3, Auspex 2]
 * @param {String} [options.specialty] nom de la spécialité utilisée (+1 dé)
 * @param {{label: String, value: Number}[]} [options.bonuses] bonus de disciplines ou d'avantages
 * @param {Number} [options.bloodSurge] Puissance du Sang si un Coup de sang est utilisé (coûte un test d'exaltation)
 * @param {Boolean} [options.impaired] jauge de Santé (physique) ou de Volonté (social/mental) pleine
 * @param {{label: String, value: Number}[]} [options.modifiers] modificateurs du MJ, positifs ou négatifs
 * @returns {{size: Number, breakdown: {label: String, value: Number, kind: String}[], requiresRouseCheck: Boolean}}
 */
export const buildPool = ({base = [], specialty, bonuses = [], bloodSurge, impaired = false, modifiers = []}) => {
  const breakdown = [
    ...base.map(part => ({...part, kind: 'base'})),
    ...(specialty ? [{label: specialty, value: SPECIALTY_BONUS, kind: 'specialty'}] : []),
    ...bonuses.map(part => ({...part, kind: 'bonus'})),
    ...(bloodSurge !== undefined && bloodSurge !== null
      ? [{label: 'bloodSurge', value: bloodSurgeBonus(bloodSurge), kind: 'bloodSurge'}]
      : []),
    ...(impaired ? [{label: 'impaired', value: IMPAIRED_PENALTY, kind: 'impaired'}] : []),
    ...modifiers.map(part => ({...part, kind: 'modifier'})),
  ].filter(part => part.value);
  const total = breakdown.reduce((sum, part) => sum + part.value, 0);
  return {
    size: Math.max(0, total),
    breakdown,
    requiresRouseCheck: breakdown.some(part => part.kind === 'bloodSurge'),
  };
};

/**
 * Répartit une réserve entre dés normaux et dés de Soif.
 * Les dés de Soif remplacent des dés normaux, jamais plus que la taille de la réserve.
 * @param {Number} size taille de la réserve
 * @param {Number} hunger niveau de Soif (0 à 5)
 * @returns {{regular: Number, hunger: Number}}
 */
export const splitPool = (size, hunger = 0) => {
  const total = Math.max(0, size || 0);
  const hungerDice = Math.min(total, Math.max(0, Math.min(MAX_HUNGER, hunger || 0)));
  return {regular: total - hungerDice, hunger: hungerDice};
};

/**
 * Évalue un jet déjà lancé.
 * Sans difficulté (connue du seul MJ), `outcome` et `margin` valent null : seuls les indicateurs sont donnés.
 * @param {{regular: Number[], hunger: Number[], difficulty?: Number}} roll
 */
export const evaluateRoll = ({regular = [], hunger = [], difficulty}) => {
  const all = [...regular, ...hunger];
  const tens = all.filter(value => value === 10).length;
  const criticalPairs = Math.floor(tens / 2);
  const successes = all.filter(isSuccess).length + criticalPairs * 2;
  const hasCritical = criticalPairs > 0;
  const regularTens = regular.filter(value => value === 10).length;
  // Sanglant seulement si un 10 de Soif fait forcément partie d'une paire :
  // les 10 normaux sont appariés en priorité entre eux.
  const hungerTenInPair = criticalPairs * 2 > regularTens;
  const hasHungerOne = hunger.includes(1);

  const flags = {
    successes,
    criticalPairs,
    hasCritical,
    // Indicateurs utiles quand la difficulté est cachée
    messyIfWin: hungerTenInPair,
    bestialIfFail: hasHungerOne,
  };

  if (difficulty === undefined || difficulty === null) {
    return {...flags, outcome: null, margin: null, isWin: null};
  }

  const isWin = successes >= difficulty;
  let outcome;
  if (isWin) {
    if (flags.messyIfWin) {
      outcome = OUTCOMES.MESSY_CRITICAL;
    } else if (hasCritical) {
      outcome = OUTCOMES.CRITICAL_WIN;
    } else {
      outcome = OUTCOMES.WIN;
    }
  } else if (hasHungerOne) {
    outcome = OUTCOMES.BESTIAL_FAILURE;
  } else if (successes === 0) {
    outcome = OUTCOMES.TOTAL_FAILURE;
  } else {
    outcome = OUTCOMES.FAILURE;
  }

  return {...flags, outcome, margin: successes - difficulty, isWin};
};

/**
 * Lance une réserve de dés.
 * @param {{size: Number, hunger?: Number, difficulty?: Number, rng?: Function}} options
 */
export const rollPool = ({size, hunger = 0, difficulty, rng = Math.random}) => {
  const split = splitPool(size, hunger);
  // Règle de table : une réserve de 0 dé (ou moins) est un échec automatique, même difficulté cachée
  if (split.regular + split.hunger === 0) {
    return {
      regular: [],
      hunger: [],
      difficulty,
      willpowerRerolled: false,
      ...evaluateRoll({regular: [], hunger: [], difficulty}),
      autoFailure: true,
      outcome: OUTCOMES.TOTAL_FAILURE,
      isWin: false,
    };
  }
  const regular = rollDice(split.regular, rng);
  const hungerDice = rollDice(split.hunger, rng);
  return {
    regular,
    hunger: hungerDice,
    difficulty,
    willpowerRerolled: false,
    ...evaluateRoll({regular, hunger: hungerDice, difficulty}),
  };
};

/**
 * Indique pourquoi une relance de Volonté est impossible (ou null si elle est possible).
 * @param {{regular: Number[], willpowerRerolled: Boolean}} roll
 * @param {Number[]} indexes index des dés normaux à relancer
 */
export const getWillpowerRerollError = (roll, indexes = []) => {
  if (roll.willpowerRerolled) return 'alreadyRerolled';
  if (indexes.length === 0) return 'noDieSelected';
  if (indexes.length > MAX_WILLPOWER_REROLL) return 'tooManyDice';
  if (new Set(indexes).size !== indexes.length) return 'duplicateDie';
  if (indexes.some(index => !Number.isInteger(index) || index < 0 || index >= roll.regular.length)) return 'invalidDie';
  return null;
};

/**
 * Relance jusqu'à 3 dés normaux (jamais les dés de Soif). Une seule fois par jet.
 * Le coût (1 dégât superficiel de Volonté) est signalé par `willpowerCost`, à appliquer par l'appelant.
 * @param {Object} roll résultat de rollPool
 * @param {Number[]} indexes index dans roll.regular
 * @param {Function} [rng]
 */
export const willpowerReroll = (roll, indexes, rng = Math.random) => {
  const error = getWillpowerRerollError(roll, indexes);
  if (error) {
    throw new Error(`Relance de Volonté impossible : ${error}`);
  }
  const regular = roll.regular.map((value, index) => (indexes.includes(index) ? rollD10(rng) : value));
  return {
    ...roll,
    regular,
    previousRegular: roll.regular,
    rerolledIndexes: [...indexes],
    willpowerRerolled: true,
    willpowerCost: 1,
    ...evaluateRoll({regular, hunger: roll.hunger, difficulty: roll.difficulty}),
  };
};

/** Niveau de discipline maximal dont le test d'exaltation peut être relancé, selon la Puissance du Sang. */
export const rouseRerollMaxLevel = (bloodPotency) => ROUSE_REROLL_MAX_LEVEL[clampBloodPotency(bloodPotency)];

/** Bonus de dés d'un Coup de sang (Blood Surge), selon la Puissance du Sang. */
export const bloodSurgeBonus = (bloodPotency) => BLOOD_SURGE_BONUS[clampBloodPotency(bloodPotency)];

/**
 * Test d'exaltation (Rouse Check) : 1d10, 6+ = pas de gain de Soif.
 * Si c'est pour une discipline dont le niveau est couvert par la Puissance du Sang, un échec est relancé une fois.
 * @param {{hunger: Number, bloodPotency?: Number, disciplineLevel?: Number, rng?: Function}} options
 */
export const rouseCheck = ({hunger = 0, bloodPotency = 0, disciplineLevel, rng = Math.random}) => {
  const dice = [rollD10(rng)];
  const canReroll = disciplineLevel !== undefined && disciplineLevel <= rouseRerollMaxLevel(bloodPotency);
  if (!isSuccess(dice[0]) && canReroll) {
    dice.push(rollD10(rng));
  }
  const success = isSuccess(dice[dice.length - 1]);
  const atMaxHunger = hunger >= MAX_HUNGER;
  return {
    dice,
    rerolled: dice.length > 1,
    success,
    hungerGain: success || atMaxHunger ? 0 : 1,
    newHunger: success ? hunger : Math.min(MAX_HUNGER, hunger + 1),
    // Échec à Soif 5 : le MJ peut demander un test de frénésie de faim
    hungerFrenzyRisk: !success && atMaxHunger,
  };
};

/**
 * Plusieurs tests d'exaltation à la suite (pouvoir qui en coûte 2…) : la Soif monte au fil des tests.
 * @param {{count: Number, hunger: Number, bloodPotency?: Number, disciplineLevel?: Number, rng?: Function}} options
 * @returns {{checks: Object[], newHunger: Number}}
 */
export const rouseChecks = ({count, hunger = 0, bloodPotency = 0, disciplineLevel, rng = Math.random}) => {
  const checks = [];
  let current = hunger;
  for (let i = 0; i < count; i += 1) {
    const check = rouseCheck({hunger: current, bloodPotency, disciplineLevel, rng});
    checks.push(check);
    current = check.newHunger;
  }
  return {checks, newHunger: current};
};

/** Dégâts superficiels de Santé soignés par un test d'exaltation, selon la Puissance du Sang. */
export const mendAmount = (bloodPotency) => MEND_AMOUNT[clampBloodPotency(bloodPotency)];

/**
 * Test de remords en fin de session : 1 dé par case d'Humanité ni remplie ni souillée (minimum 1).
 * Un seul succès suffit. Réussite : souillures effacées. Échec : -1 Humanité, souillures effacées.
 * @param {{humanity: Number, stains: Number, rng?: Function}} options
 */
export const remorseCheck = ({humanity, stains, rng = Math.random}) => {
  if (!stains) {
    return {required: false, dice: [], success: true, newHumanity: humanity, newStains: 0};
  }
  const size = Math.max(1, 10 - humanity - stains);
  const dice = rollDice(size, rng);
  const success = dice.some(isSuccess);
  return {
    required: true,
    dice,
    success,
    newHumanity: success ? humanity : Math.max(0, humanity - 1),
    newStains: 0,
  };
};

/**
 * Réserve d'un test de frénésie : Volonté restante + un tiers de l'Humanité (arrondi à l'inférieur). Pas de dés de Soif.
 * @param {{willpower: Number, humanity: Number}} options willpower = cases de Volonté non endommagées
 */
export const frenzyPoolSize = ({willpower, humanity}) => Math.max(0, willpower) + Math.floor(Math.max(0, humanity) / 3);

export const frenzyCheck = ({willpower, humanity, difficulty, rng = Math.random}) => {
  const size = frenzyPoolSize({willpower, humanity});
  const regular = rollDice(size, rng);
  const result = evaluateRoll({regular, hunger: [], difficulty});
  return {size, regular, hunger: [], ...result, resisted: result.isWin};
};
